import React from 'react';
import { PrismicRichText } from '@prismicio/react';
import styled from 'styled-components';
import { BREAKPOINT, CONTENT_MAX_WIDTH_PX } from '@constants';
import { PageSlice, SliceLinkTabsDetails, SliceLinkTabsItem } from '@contracts';
import ArrowRightIcon from '@images/icon-arrow-right.svg';
import Colors from '../Colors';
import LinkTranslated from '../LinkTranslated';
import { TEXT_STYLES, TextVariant, Typography } from './Typography';

interface LinkTabsProps {
  slice: PageSlice;
}

const LinkTabs: React.FC<LinkTabsProps> = ({ slice }) => {
  const sliceDetails = slice.primary as SliceLinkTabsDetails;
  const sliceItems = slice.items as SliceLinkTabsItem[];

  return (
    <GridContainer $background={sliceDetails?.background_text}>
      <Wrapper>
        <HeadingWrapper>
          {sliceDetails?.overline && <Typography $variant={TextVariant.UpperText4}>{sliceDetails.overline}</Typography>}
          <Heading>
            <PrismicRichText field={sliceDetails.heading.richText} />
          </Heading>
        </HeadingWrapper>
        <Grid>
          {sliceItems.map((item, index) => (
            <LinkTab key={index} to={item.button_url}>
              <ImageContainer>{item.image.url && <img src={item.image.url} alt={item.image.alt ?? item.title.text} />}</ImageContainer>
              <TabHeading>
                <PrismicRichText field={item.title.richText} />
              </TabHeading>
              <ArrowRightIcon style={{ flexShrink: 0 }} />
            </LinkTab>
          ))}
        </Grid>
      </Wrapper>
    </GridContainer>
  );
};

export default LinkTabs;

const GridContainer = styled.section<{ $background?: string }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  background: ${(props) => props.$background || 'transparent'};
  color: ${Colors.navyBlue};
  width: 100%;
`;

const Wrapper = styled.div`
  width: 90vw;
  max-width: ${CONTENT_MAX_WIDTH_PX};
  margin: 0 auto;
  padding: 64px 0;
  display: flex;
  flex-direction: column;
  gap: 48px;

  @media (max-width: 1024px) {
    padding: 56px 0;
    gap: 24px;
  }
`;

const HeadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
`;

const Heading = styled.div`
  > * {
    color: ${Colors.navyBlue};
    ${TEXT_STYLES[TextVariant.Heading3]};

    @media (max-width: ${BREAKPOINT.mobileL}px) {
      ${TEXT_STYLES[TextVariant.Heading5]};
    }
  }
`;

const Grid = styled.div`
  width: 100%;
  display: grid;
  max-width: 100%;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: ${BREAKPOINT.laptop}px) {
    gap: 8px;
  }
`;

const LinkTab = styled(LinkTranslated)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 30px 24px;
  border: 1px solid ${Colors.ecru};
  border-radius: 12px;
  background: ${Colors.cream};
  transition: background 0.3s ease-in-out;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: ${BREAKPOINT.laptop}px) {
    flex-direction: column;
    justify-content: center;
    padding: 12px 6px;
  }

  &:hover {
    text-decoration: none;

    @media (min-width: ${BREAKPOINT.laptop}px) {
      background: ${Colors.paleGold25};
    }
  }

  & svg {
    @media (max-width: ${BREAKPOINT.laptop}px) {
      display: none;
    }
  }
`;

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  overflow: hidden;
`;

const TabHeading = styled.div`
  flex: 1 1 auto;
  width: 100%;
  display: flex;
  align-items: center;

  > * {
    flex-grow: 1;
    width: 100%;
    color: ${Colors.navyBlue};
    ${TEXT_STYLES[TextVariant.UpperText3]};

    @media (max-width: ${BREAKPOINT.laptop}px) {
      ${TEXT_STYLES[TextVariant.UpperText6]};
      text-align: center;
    }
  }
`;
